import { css, html, LitElement, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import type { Commerce, CommerceCustomerSession } from "@teqtank/commerce-sdk";

@customElement("teqtank-login")
export class TeqtankLogin extends LitElement {
    @property({ attribute: false }) public accessor commerce: Commerce | undefined;
    @property({ attribute: "back-office-label" })
    public accessor backOfficeLabel = "Open Back Office";
    @state() private accessor mode: "password" | "code" = "password";
    @state() private accessor identifier = "";
    @state() private accessor email = "";
    @state() private accessor password = "";
    @state() private accessor code = "";
    @state() private accessor challengeId = "";
    @state() private accessor maskedDestination = "";
    @state() private accessor rememberMe = false;
    @state() private accessor busy = false;
    @state() private accessor session: CommerceCustomerSession | undefined;
    @state() private accessor errorMessage = "";
    private unsubscribe: (() => void) | undefined;
    public static override styles = css`
        :host {
            container-type: inline-size;
            display: block;
            color: var(--teqtank-color-text, #18221e);
            font-family: var(--teqtank-font-body, Inter, ui-sans-serif, system-ui, sans-serif);
        }
        .card {
            display: grid;
            gap: 1rem;
            padding: clamp(1rem, 4cqw, 1.5rem);
            border: 1px solid var(--teqtank-color-border, #ded8cf);
            border-radius: var(--teqtank-login-radius, 1rem);
            background: var(--teqtank-login-background, var(--teqtank-color-surface, #fff));
        }
        h2,
        p {
            margin: 0;
        }
        h2 {
            font-family: var(--teqtank-font-heading, Georgia, serif);
            font-size: 1.55rem;
        }
        p,
        small {
            color: var(--teqtank-color-text-muted, #69736d);
        }
        .tabs {
            display: grid;
            grid-template-columns: 1fr 1fr;
            padding: 4px;
            background: var(--teqtank-color-surface-soft, #eee8df);
            border-radius: 999px;
        }
        .tabs button {
            background: transparent;
            color: inherit;
        }
        .tabs button.active {
            background: #fff;
            color: inherit;
            box-shadow: 0 1px 5px rgba(0, 0, 0, 0.08);
        }
        form {
            display: grid;
            gap: 0.9rem;
        }
        label {
            display: grid;
            gap: 0.4rem;
            font-size: 0.82rem;
            font-weight: 700;
        }
        input {
            width: 100%;
            box-sizing: border-box;
            border: 1px solid var(--teqtank-color-border, #ded8cf);
            border-radius: 0.7rem;
            padding: 0.85rem 0.95rem;
            font: inherit;
            background: #fff;
        }
        .remember {
            display: flex;
            align-items: center;
            gap: 0.55rem;
            font-weight: 500;
        }
        .remember input {
            width: auto;
        }
        .actions {
            display: flex;
            gap: 0.65rem;
            flex-wrap: wrap;
        }
        button,
        a {
            display: inline-flex;
            justify-content: center;
            border: 0;
            border-radius: 999px;
            padding: 0.85rem 1.1rem;
            font: inherit;
            font-weight: 750;
            text-decoration: none;
            cursor: pointer;
        }
        button.primary {
            background: var(--teqtank-color-primary, #18221e);
            color: var(--teqtank-color-on-primary, #fff);
        }
        button.secondary {
            background: transparent;
            border: 1px solid var(--teqtank-color-border, #ded8cf);
            color: inherit;
        }
        button:disabled {
            opacity: 0.55;
            cursor: wait;
        }
        .error {
            padding: 0.75rem;
            border-radius: 0.65rem;
            background: #fff2f2;
            color: #9c2f2f;
            font-size: 0.85rem;
        }
        .preview {
            padding: 0.75rem;
            border-radius: 0.65rem;
            background: var(--teqtank-color-surface-soft, #eee8df);
            color: var(--teqtank-color-text-muted, #69736d);
            font-size: 0.85rem;
        }
        .account {
            display: grid;
            gap: 0.5rem;
        }
        .account strong {
            font-size: 1.05rem;
        }
        @container (max-width:420px) {
            .tabs {
                grid-template-columns: 1fr;
            }
            .actions > * {
                width: 100%;
            }
        }
    `;
    public override connectedCallback(): void {
        super.connectedCallback();
        this.bindCommerce();
    }
    protected override updated(changed: Map<PropertyKey, unknown>): void {
        if (changed.has("commerce")) this.bindCommerce();
    }
    public override disconnectedCallback(): void {
        this.unsubscribe?.();
        super.disconnectedCallback();
    }
    protected override render() {
        if (this.session?.status === "authenticated" && this.session.customer) {
            return html`<section class="card account" part="card account">
                <small part="eyebrow">SIGNED IN</small
                ><strong part="customer-name">${this.session.customer.displayName}</strong
                ><span part="customer-email">${this.session.customer.email}</span>
                <div class="actions" part="actions">
                    ${
                        this.session.backOfficeUrl
                            ? html`<a
                                  class="secondary"
                                  part="secondary-action back-office-action"
                                  href=${this.session.backOfficeUrl}
                                  >${this.backOfficeLabel}</a
                              >`
                            : nothing
                    }<button
                        class="secondary"
                        part="secondary-action sign-out"
                        @click=${this.logout}
                    >
                        Sign out
                    </button>
                </div>
            </section>`;
        }

        const supportsEmailCode = this.supportsEmailCode;
        const previewsEmailCode = this.previewsEmailCode;

        return html`<section class="card" part="card">
            <div part="header">
                <small part="eyebrow">EXISTING CUSTOMER</small>
                <h2 part="title">Sign in to your account</h2>
                <p part="description">
                    ${
                        supportsEmailCode
                            ? "Use your password or verify your email with a one-time code."
                            : "Use your username or email and password."
                    }
                </p>
            </div>
            ${
                supportsEmailCode
                    ? html`<div class="tabs" part="tabs">
                          <button
                              part="tab password-tab"
                              class=${this.mode === "password" ? "active" : ""}
                              @click=${() => (this.mode = "password")}
                          >
                              Password</button
                          ><button
                              part="tab code-tab"
                              class=${this.mode === "code" ? "active" : ""}
                              @click=${() => (this.mode = "code")}
                          >
                              Email code
                          </button>
                      </div>`
                    : nothing
            }
            ${
                this.errorMessage
                    ? html`<div class="error" part="error">${this.errorMessage}</div>`
                    : nothing
            }${
                previewsEmailCode && this.mode === "code"
                    ? html`<div class="preview" part="notice">
                          A one-time code will be sent to your email for verification.
                      </div>`
                    : nothing
            }${
                !supportsEmailCode || this.mode === "password"
                    ? this.renderPassword()
                    : this.renderCode()
            }
        </section>`;
    }

    private renderPassword() {
        return html`<form part="form password-form" @submit=${this.login}>
            <label
                >Username or email<input
                    autocomplete="username"
                    .value=${this.identifier}
                    @input=${(event: InputEvent) =>
                        (this.identifier = (event.currentTarget as HTMLInputElement).value)}
                    required /></label
            ><label
                >Password<input
                    type="password"
                    autocomplete="current-password"
                    .value=${this.password}
                    @input=${(event: InputEvent) =>
                        (this.password = (event.currentTarget as HTMLInputElement).value)}
                    required /></label
            >${this.remember()}<button class="primary" part="primary-action" ?disabled=${this.busy}>
                ${this.busy ? "Signing in…" : "Sign in"}
            </button>
        </form>`;
    }

    private renderCode() {
        if (this.challengeId) {
            return html`<form part="form code-form" @submit=${this.verify}>
                <p>Code sent to <strong>${this.maskedDestination}</strong></p>
                <label
                    >Verification code<input
                        inputmode="numeric"
                        autocomplete="one-time-code"
                        .value=${this.code}
                        @input=${(event: InputEvent) =>
                            (this.code = (event.currentTarget as HTMLInputElement).value)}
                        required /></label
                >${this.remember()}
                <div class="actions" part="actions">
                    <button class="primary" part="primary-action" ?disabled=${this.busy}>
                        Verify</button
                    ><button
                        class="secondary"
                        part="secondary-action"
                        type="button"
                        @click=${this.sendCode}
                    >
                        Resend
                    </button>
                </div>
            </form>`;
        }

        return html`<form part="form code-request-form" @submit=${this.sendCode}>
            <label
                >Email address<input
                    type="email"
                    autocomplete="email"
                    .value=${this.email}
                    @input=${(event: InputEvent) =>
                        (this.email = (event.currentTarget as HTMLInputElement).value)}
                    required /></label
            ><button class="primary" part="primary-action" ?disabled=${this.busy}>
                Send verification code
            </button>
        </form>`;
    }

    private remember() {
        return html`<label class="remember" part="remember-me"
            ><input
                type="checkbox"
                ?checked=${this.rememberMe}
                @change=${(event: Event) =>
                    (this.rememberMe = (event.currentTarget as HTMLInputElement).checked)}
            />Keep me signed in</label
        >`;
    }

    private bindCommerce() {
        this.unsubscribe?.();
        this.unsubscribe = undefined;
        if (!this.commerce) return;
        this.session = this.commerce.customer.session;
        this.unsubscribe = this.commerce.customer.onChanged(
            (event) => (this.session = event.session),
        );
    }

    private readonly login = async (event: SubmitEvent) => {
        event.preventDefault();
        await this.run(async () => {
            await this.requireCommerce().customer.login({
                identifier: this.identifier,
                password: this.password,
                rememberMe: this.rememberMe,
            });
            this.emitLogin();
        });
    };

    private readonly sendCode = async (event?: Event) => {
        event?.preventDefault();
        await this.run(async () => {
            if (this.previewsEmailCode) {
                this.challengeId = "demo-preview";
                this.maskedDestination = this.maskEmail(this.email);
                return;
            }
            const challenge = await this.requireCommerce().customer.requestVerificationCode(
                this.email,
            );
            this.challengeId = challenge.challengeId;
            this.maskedDestination = challenge.maskedDestination;
        });
    };

    private readonly verify = async (event: SubmitEvent) => {
        event.preventDefault();
        await this.run(async () => {
            if (this.previewsEmailCode) {
                throw new Error("Email-code sign-in is a demo preview and is not connected yet.");
            }
            await this.requireCommerce().customer.verifyCode({
                challengeId: this.challengeId,
                code: this.code,
                rememberMe: this.rememberMe,
            });
            this.emitLogin();
        });
    };

    private readonly logout = async () => {
        this.session = await this.requireCommerce().customer.logout();
        this.requestUpdate();
    };

    private async run(action: () => Promise<void>) {
        this.busy = true;
        this.errorMessage = "";
        try {
            await action();
        } catch (error) {
            this.errorMessage = error instanceof Error ? error.message : "Sign in failed.";
        } finally {
            this.busy = false;
        }
    }

    private emitLogin() {
        this.dispatchEvent(
            new CustomEvent("teqtank-customer-login", {
                detail: { session: this.requireCommerce().customer.session },
                bubbles: true,
                composed: true,
            }),
        );
    }

    private requireCommerce() {
        if (!this.commerce) throw new Error("Commerce runtime is unavailable.");
        return this.commerce;
    }

    private get supportsEmailCode(): boolean {
        return (
            this.commerce?.context.features.has("email-verification-code") === true ||
            this.previewsEmailCode
        );
    }

    private get previewsEmailCode(): boolean {
        return this.commerce?.context.features.has("email-verification-code-preview") ?? false;
    }

    private maskEmail(value: string): string {
        const [local = "", domain = ""] = value.trim().split("@", 2);
        if (!local || !domain) return value;
        return `${local.slice(0, 1)}${"•".repeat(Math.max(2, local.length - 1))}@${domain}`;
    }
}
declare global {
    interface HTMLElementTagNameMap {
        "teqtank-login": TeqtankLogin;
    }
}
